const LA_TIME_ZONE = 'America/Los_Angeles';

export const calculateEstimatedClassEarnings = (classData = {}) => {
  const start = Number(classData.Date || 0);
  const end = Number(classData.EndDate || 0);
  const durationMinutes = end > start ? Math.round((end - start) / 60000) : 0;
  const surge = Number(classData.SurgePricingRate || 0);

  return durationMinutes + surge;
};

export const getPayoutClassId = (payout = {}) => (
  payout.classID
  || payout.ClassID
  || payout.classId
  || payout.ClassId
  || payout.class_id
  || payout.classDocumentId
  || payout.classDocumentID
  || payout.ClassDocumentId
  || payout.ClassDocumentID
  || payout.classDocId
  || payout.classDocID
  || payout.ClassDocId
  || payout.ClassDocID
  || ''
);

export const getPayoutReferenceId = (payout = {}) => (
  payout.payoutReferenceID
  || payout.PayoutReferenceID
  || payout.payoutReferenceId
  || payout.ReferenceID
  || payout.referenceId
  || ''
);

export const getPayoutAmount = (payout = {}) => {
  const directAmount = payout.amount ?? payout.Amount ?? payout.payoutAmount ?? payout.PayoutAmount;
  if (directAmount !== undefined && directAmount !== null && directAmount !== '') {
    return Number(directAmount);
  }

  const cents = payout.amountCents ?? payout.AmountCents;
  return cents !== undefined && cents !== null && cents !== '' ? Number(cents) / 100 : 0;
};

export const getPayoutStatus = (payout = {}) => (
  payout.status
  || payout.Status
  || payout.paymentStatus
  || payout.PaymentStatus
  || ''
);

export const getPayoutStripeTransferStatus = (payout = {}) => {
  const normalizedStatus = String(getPayoutStatus(payout)).trim().toUpperCase();
  const isSuccess = normalizedStatus === 'SUCCESS';

  return {
    isSuccess,
    label: isSuccess ? 'Transferred to Stripe' : 'Pending',
    colorScheme: isSuccess ? 'green' : 'orange',
  };
};

export const getClassCompletionDate = (classData = {}, payout = {}) => (
  classData.InstructorCompletedAt
  || classData.instructorCompletedAt
  || classData.completedAt
  || classData.CompletedAt
  || classData.completionDate
  || classData.CompletedDate
  || classData.ClassCompletionDate
  || classData.classCompletionDate
  || payout.classCompletionDate
  || payout.ClassCompletionDate
  || payout.completedAt
  || payout.CompletedAt
  || ''
);

export const getPayoutDateMs = (payout = {}) => {
  const dateValue = payout.date || payout.Date || payout.payoutDate || payout.PayoutDate;

  if (!dateValue) return 0;
  if (typeof dateValue.toMillis === 'function') return dateValue.toMillis();
  if (typeof dateValue.toDate === 'function') return dateValue.toDate().getTime();
  if (dateValue instanceof Date) return dateValue.getTime();
  if (typeof dateValue === 'number') return dateValue;

  const parsedDate = new Date(dateValue);
  const parsedMs = parsedDate.getTime();
  return Number.isNaN(parsedMs) ? 0 : parsedMs;
};

export const formatCurrencyFromCents = (cents = 0) => (
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Number(cents || 0) / 100)
);

export const getPayoutTransferDateLabel = (payout = {}) => {
  const payoutDateMs = getPayoutDateMs(payout);
  if (!payoutDateMs) return 'Payout date unavailable';

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(payoutDateMs));
};

const formatMonthYear = (month, year) => {
  if (!month || !year) return '';
  return `${String(month).padStart(2, '0')}/${year}`;
};

export const getStripePaymentMethodSummary = (stripeStatus = {}, now = new Date()) => {
  const account = Array.isArray(stripeStatus?.external_accounts)
    ? stripeStatus.external_accounts[0]
    : null;

  if (!account) {
    return {
      type: 'unknown',
      title: 'Payment method on file',
      detail: 'Stripe payout method details are not available yet.',
      meta: '',
      expiresSoon: false,
    };
  }

  if (account.object === 'card') {
    const brand = account.brand || 'Card';
    const funding = account.funding ? `${account.funding} ` : '';
    const expMonth = Number(account.exp_month || 0);
    const expYear = Number(account.exp_year || 0);
    const expirationDate = expMonth && expYear ? new Date(expYear, expMonth, 0, 23, 59, 59) : null;
    const warningWindowMs = 90 * 24 * 60 * 60 * 1000;
    const expiresSoon = Boolean(
      expirationDate && expirationDate.getTime() - now.getTime() <= warningWindowMs
    );

    return {
      type: 'card',
      title: `${brand} ${funding}card`.replace(/\s+/g, ' ').trim(),
      detail: account.last4 ? `Ending in ${account.last4}` : 'Card on file',
      meta: formatMonthYear(expMonth, expYear) ? `Expires ${formatMonthYear(expMonth, expYear)}` : '',
      expiresSoon,
    };
  }

  if (account.object === 'bank_account') {
    const currency = account.currency ? String(account.currency).toUpperCase() : '';
    const country = account.country || '';

    return {
      type: 'bank',
      title: account.bank_name || 'Bank account',
      detail: account.last4 ? `Account ending in ${account.last4}` : 'Bank account on file',
      meta: [currency, country].filter(Boolean).join(' · '),
      expiresSoon: false,
    };
  }

  return {
    type: 'unknown',
    title: 'Payment method on file',
    detail: account.last4 ? `Ending in ${account.last4}` : 'Stripe payout method connected',
    meta: account.currency ? String(account.currency).toUpperCase() : '',
    expiresSoon: false,
  };
};

export const getStripeBalanceSummary = (stripeStatus = {}) => {
  const balance = stripeStatus?.balance || {};
  const transferVolume = stripeStatus?.allTimeTransferVolume || {};

  return {
    available: formatCurrencyFromCents(balance.availableCents),
    pending: formatCurrencyFromCents(balance.pendingCents),
    total: formatCurrencyFromCents(balance.totalCents),
    allTimeVolume: formatCurrencyFromCents(transferVolume.totalCents),
    transferCount: Number(transferVolume.transferCount || 0),
  };
};

export const getInstructorPayoutItems = (instructor = {}) => {
  const active = [
    ...(Array.isArray(instructor.payouts) ? instructor.payouts : []),
    ...(Array.isArray(instructor.Payouts) ? instructor.Payouts : []),
  ];
  const reversed = [
    ...(Array.isArray(instructor.reversedPayouts) ? instructor.reversedPayouts : []),
    ...(Array.isArray(instructor.ReversedPayouts) ? instructor.ReversedPayouts : []),
  ];

  return [
    ...active.map((payout) => ({ ...payout, payoutState: 'active' })),
    ...reversed.map((payout) => ({ ...payout, payoutState: 'reversed' })),
  ].sort((left, right) => getPayoutDateMs(right) - getPayoutDateMs(left));
};

const getLADateParts = (dateMs) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: LA_TIME_ZONE,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    weekday: 'short',
  }).formatToParts(new Date(dateMs));

  return Object.fromEntries(parts.map((part) => [part.type, part.value]));
};

export const getLAWeekRange = (dateMs = Date.now()) => {
  const parts = getLADateParts(dateMs);
  const weekdayIndex = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(parts.weekday);
  const laMidnightUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    7,
    0,
    0,
    0
  );
  const startMs = laMidnightUtc - weekdayIndex * 24 * 60 * 60 * 1000;

  return {
    startMs,
    endMs: startMs + 7 * 24 * 60 * 60 * 1000 - 1,
  };
};
